<template>
  <div class="keyword-reply-container">
    <div class="section-title">关键词回复设置</div>
    <p class="section-description">
      设置特定关键词的自动回复内容。当用户发送包含这些关键词的消息时，系统将自动回复预设内容。
    </p>

    <!-- AC-FE-SELECTOR-001: 소유 공중계정이 2개 이상이면 셀렉터 표시(欢迎语 탭과 동일 패턴) -->
    <div v-if="ownedGzhList.length > 1" class="gzh-selector mb-3">
      <a-select
        v-model:value="selectedGzhName"
        style="min-width: 260px;"
        @change="onGzhChange"
      >
        <a-select-option
          v-for="acc in ownedGzhList"
          :key="acc.gzhName"
          :value="acc.gzhName"
        >
          {{ acc.displayName }}（{{ acc.accountType }}）
        </a-select-option>
      </a-select>
    </div>

    <!-- AC-FE-EMPTY-001: 소유 공중계정이 0개면 오류 없이 빈 상태만 표시 -->
    <div v-if="ownedGzhList.length === 0" class="gzh-empty-notice">
      <a-empty description="没有可管理的公众号" />
    </div>

    <template v-else>
      <a-alert
        v-if="errorMsg"
        type="error"
        :message="errorMsg"
        banner
        closable
        @close="errorMsg = ''"
        class="mb-3"
      />

      <div class="keyword-actions mb-3">
        <a-button type="primary" @click="showAddKeywordModal" class="action-button">
          <template #icon><plus-outlined /></template>
          添加关键词回复
        </a-button>
        <a-input-search
          v-model:value="searchKeyword"
          placeholder="搜索关键词"
          style="width: 250px; margin-left: 16px;"
          @search="handleSearch"
          class="search-input"
        />
      </div>

      <a-spin :spinning="loading">
        <div v-if="keywords.length > 0" class="keyword-list">
          <div v-if="!isMobileView">
            <a-table
              :dataSource="keywords"
              :columns="columns"
              :pagination="pagination"
              :scroll="{ x: 700 }"
              @change="handleTableChange"
              rowKey="id"
            >
              <template #bodyCell="{ column, record }">
                <!-- 키워드 내용 열 -->
                <template v-if="column.key === 'content'">
                  <span>{{ record.content }}</span>
                </template>

                <!-- 매치 타입 열 -->
                <template v-if="column.key === 'matchType'">
                  <a-tag :color="record.matchType === 'EXACT' ? 'blue' : 'green'">
                    {{ getMatchTypeLabel(record.matchType) }}
                  </a-tag>
                </template>

                <!-- 상태 열 -->
                <template v-if="column.key === 'status'">
                  <a-switch
                    :checked="record.isActive"
                    :loading="record.id === loadingItemId"
                    @change="(checked) => toggleKeywordStatus(record.id, checked)"
                  />
                </template>

                <!-- 작업 열 -->
                <template v-if="column.key === 'action'">
                  <a-space>
                    <a-button type="link" @click="handlePreview(record)">预览</a-button>
                    <a-button type="link" @click="handleViewResponses(record)">查看回复</a-button>
                    <a-button type="link" @click="handleEditKeyword(record)">编辑</a-button>
                    <a-popconfirm
                      title="确定要删除此关键词吗？"
                      ok-text="确定"
                      cancel-text="取消"
                      @confirm="handleDeleteKeyword(record.id)"
                    >
                      <a-button type="link" danger>删除</a-button>
                    </a-popconfirm>
                  </a-space>
                </template>
              </template>
            </a-table>
          </div>

          <!-- 모바일 카드뷰 -->
          <div v-else class="mobile-card-list">
            <div v-for="record in keywords" :key="record.id" class="mobile-company-card">
              <div class="card-field">
                <span class="card-label">关键词:</span>
                <span>{{ record.content }}</span>
              </div>
              <div class="card-field">
                <span class="card-label">匹配方式:</span>
                <a-tag :color="record.matchType === 'EXACT' ? 'blue' : 'green'">
                  {{ getMatchTypeLabel(record.matchType) }}
                </a-tag>
              </div>
              <div class="card-field">
                <span class="card-label">状态:</span>
                <a-switch
                  :checked="record.isActive"
                  :loading="record.id === loadingItemId"
                  @change="(checked) => toggleKeywordStatus(record.id, checked)"
                />
              </div>
              <div class="card-actions">
                <a-button size="small" @click="handlePreview(record)">预览</a-button>
                <a-button size="small" @click="handleViewResponses(record)">查看回复</a-button>
                <a-button size="small" @click="handleEditKeyword(record)">编辑</a-button>
                <a-popconfirm
                  title="确定要删除此关键词吗？"
                  ok-text="确定"
                  cancel-text="取消"
                  @confirm="handleDeleteKeyword(record.id)"
                >
                  <a-button size="small" danger>删除</a-button>
                </a-popconfirm>
              </div>
            </div>
            <a-pagination
              v-if="pagination.total > 0"
              :current="pagination.current"
              :page-size="pagination.pageSize"
              :total="pagination.total"
              :show-total="pagination.showTotal"
              style="margin-top: 16px; text-align: right;"
              @change="(page, pageSize) => handleTableChange({ current: page, pageSize })"
            />
          </div>
        </div>
        <div v-else class="empty-state">
          <a-empty description="暂无关键词回复设置" />
        </div>
      </a-spin>

      <!-- 레거시와 동일한 등록/응답 모달 컴포넌트를 재사용한다(순수 emit 기반, API 무관) -->
      <keyword-register-modal
        :visible="isModalVisible"
        @update:visible="isModalVisible = $event"
        :confirm-loading="confirmLoading"
        :is-editing="isEditing"
        :keyword="currentKeyword"
        @cancel="handleModalCancel"
        @submit="handleModalSubmit"
      />

      <keyword-reply-modal
        :visible="isResponseModalVisible"
        @update:visible="isResponseModalVisible = $event"
        :keyword="selectedKeyword"
        :responses="keywordResponses"
        :loading="loadingResponses"
        :loadingId="loadingItemId"
        @toggle-status="handleResponseStatusToggle"
        @delete-response="handleDeleteResponse"
        @create-response="handleCreateResponse"
        @update-response="handleUpdateResponse"
        @preview="() => handlePreview(selectedKeyword)"
      />
    </template>
  </div>
</template>

<script>
import { ref, reactive, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { PlusOutlined } from '@ant-design/icons-vue';
import { message } from 'ant-design-vue';
import gzhApis from '../../api';
import KeywordRegisterModal from './KeywordRegisterModal.vue';
import KeywordReplyModal from './KeywordReplyModal.vue';

// SPEC-CHINA-GZH-KEYWORD-TENANT-001 M4: dentalmax(+미래 테넌트) 전용 关键词回复 관리 화면.
// china-inapi keywordTenant API(?name=<gzhName> 소유 스코프)를 사용한다. Dentium은 이 컴포넌트를 절대 렌더링하지 않는다
// (AutoReplayManagement.vue의 per-GZH 라우팅에서 배제됨).
export default {
  name: 'TenantKeywordManagement',
  components: {
    PlusOutlined,
    KeywordRegisterModal,
    KeywordReplyModal
  },
  props: {
    // 소유 공중계정 목록(부모 AutoReplayManagement.vue가 이미 조회한 목록을 그대로 전달) — Dentium 미포함
    ownedGzhList: {
      type: Array,
      default: () => []
    },
    // 선택된 키워드 응답(欢迎语 탭과 무관, 关键词回复 탭 미리보기 전용 v-model)
    selectedKeywordReply: {
      type: [String, Object],
      default: null
    }
  },
  // update:selected-gzh-name — 关键词回复 탭에서 현재 선택된 공중계정을 부모(AutoReplayManagement.vue)로 끌어올려
  // MobileEmulator 헤더/아바타(chat-title/logo-text/logo-sub-text)를 실제 선택 GZH에 맞춰 표시하도록 한다.
  emits: ['update:selected-keyword-reply', 'update:selected-gzh-name'],
  setup(props, { emit }) {
    const apis = gzhApis();

    // 모바일 카드뷰 전환 — Composition API 컴포넌트라 하우스 공용 mixin(dealerEcountListMixin, Options API 전용)을
    // 그대로 쓸 수 없어 동일 breakpoint(767px)로 로컬 구현한다.
    const isMobileView = ref(window.innerWidth <= 767);
    const onResize = () => { isMobileView.value = window.innerWidth <= 767; };

    // 소유 공중계정 셀렉터 상태(欢迎语 탭 패턴 재사용, AC-FE-SELECTOR-002/003)
    const selectedGzhName = ref(props.ownedGzhList.length > 0 ? props.ownedGzhList[0].gzhName : '');

    const keywords = ref([]);
    const keywordResponses = ref([]);
    const selectedKeyword = ref(null);
    const currentKeyword = ref(null);
    const searchKeyword = ref('');
    const loading = ref(false);
    const loadingResponses = ref(false);
    const loadingItemId = ref(null);
    const errorMsg = ref('');
    const isModalVisible = ref(false);
    const isResponseModalVisible = ref(false);
    const confirmLoading = ref(false);
    const isEditing = ref(false);
    const previewContent = ref({ keyword: '', response: '' });

    const pagination = reactive({
      current: 1,
      pageSize: 10,
      total: 0,
      showSizeChanger: true,
      showTotal: (total) => `共 ${total} 项`
    });

    const columns = [
      { title: '关键词', dataIndex: 'content', key: 'content', width: '35%' },
      { title: '匹配方式', dataIndex: 'matchType', key: 'matchType', width: '20%' },
      { title: '状态', key: 'status', width: '10%' },
      { title: '操作', key: 'action', width: '35%' }
    ];

    const getMatchTypeLabel = (matchType) => (matchType === 'EXACT' ? '完全匹配' : '包含匹配');

    // BE 계약(toItemDto/toResponseItemDto)은 `id`를 반환한다(레거시 `_id`가 아님, 프로덕션 결함 수정).
    // 이 컴포넌트 내부 로직은 `id`를 정본으로 사용한다. 다만 재사용 레거시 모달
    // (KeywordRegisterModal/KeywordReplyModal/ResponseRegisterModal)은 입력 데이터 계약상 `_id` 필드를
    // 그대로 읽으므로(그 모달들은 关键词回复 레거시 탭도 서빙하여 수정 불가), prop 경계에서만 `_id` 별칭을
    // 부여해 모달 파일은 절대 건드리지 않는다.
    const withLegacyIdAlias = (item) => ({ ...item, _id: item.id });

    // [HARD] 파괴적/변경 호출 가드 — id가 null/undefined/빈 문자열이면 절대 요청을 보내지 않는다
    // (DELETE .../keywords/undefined 500 프로덕션 결함 재발 방지).
    const isValidId = (id) => id !== undefined && id !== null && id !== '';
    const warnInvalidId = (op) => {
      console.warn(`[TenantKeywordManagement] ${op} skipped: invalid id`);
      message.error('操作失败：无效的记录标识');
    };

    // 키워드 목록 조회 (선택 gzhName 스코프)
    const fetchKeywords = async () => {
      if (!selectedGzhName.value) return;
      try {
        loading.value = true;
        errorMsg.value = '';

        const response = await apis.keywordTenant.keywords.getList(selectedGzhName.value, {
          page: pagination.current,
          limit: pagination.pageSize
        });

        keywords.value = (response.data?.items || []).map(withLegacyIdAlias);
        pagination.total = response.data?.total || 0;
      } catch (error) {
        console.error('Error fetching tenant keywords:', error);
        errorMsg.value = '获取关键词列表失败: ' + (error.response?.data?.message || error.message);
      } finally {
        loading.value = false;
      }
    };

    const fetchResponses = async (keywordId) => {
      if (!selectedGzhName.value) return;
      if (!isValidId(keywordId)) {
        warnInvalidId('fetchResponses');
        return;
      }
      try {
        loadingResponses.value = true;

        const response = await apis.keywordTenant.responses.getByKeywordId(selectedGzhName.value, keywordId);
        keywordResponses.value = (response.data?.items || []).map(withLegacyIdAlias);
      } catch (error) {
        console.error('Error fetching tenant responses:', error);
      } finally {
        loadingResponses.value = false;
      }
    };

    const toggleKeywordStatus = async (id, isActive) => {
      if (!isValidId(id)) {
        warnInvalidId('toggleKeywordStatus');
        return;
      }
      try {
        loadingItemId.value = id;
        await apis.keywordTenant.keywords.toggleStatus(selectedGzhName.value, id, isActive);
        fetchKeywords();
      } catch (error) {
        console.error('Error toggling tenant keyword status:', error);
        errorMsg.value = '更改关键词状态失败: ' + (error.response?.data?.message || error.message);
      } finally {
        loadingItemId.value = null;
      }
    };

    const handleTableChange = (pag) => {
      pagination.current = pag.current;
      pagination.pageSize = pag.pageSize;
      fetchKeywords();
    };

    const handleSearch = () => {
      pagination.current = 1;
      fetchKeywords();
    };

    const showAddKeywordModal = () => {
      isEditing.value = false;
      currentKeyword.value = {
        _id: null,
        content: '',
        matchType: 'EXACT',
        priority: 1,
        isActive: true
      };
      isModalVisible.value = true;
    };

    const handleEditKeyword = (record) => {
      isEditing.value = true;
      currentKeyword.value = { ...record };
      isModalVisible.value = true;
    };

    const handleModalCancel = () => {
      isModalVisible.value = false;
    };

    const handleModalSubmit = async (formData) => {
      // formData._id는 KeywordRegisterModal(레거시 모달 재사용, `keyword` prop의 `_id` 별칭에서 유래)이 채워준다.
      if (isEditing.value && !isValidId(formData._id)) {
        warnInvalidId('handleModalSubmit(update)');
        return;
      }
      try {
        confirmLoading.value = true;

        if (isEditing.value) {
          await apis.keywordTenant.keywords.update(selectedGzhName.value, formData._id, {
            content: formData.content,
            matchType: formData.matchType,
            priority: formData.priority,
            isActive: formData.isActive
          });
        } else {
          await apis.keywordTenant.keywords.create(selectedGzhName.value, {
            content: formData.content,
            matchType: formData.matchType,
            priority: formData.priority
          });
        }

        isModalVisible.value = false;
        fetchKeywords();
      } catch (error) {
        console.error('Error saving tenant keyword:', error);
        errorMsg.value = '保存关键词失败: ' + (error.response?.data?.message || error.message);
      } finally {
        confirmLoading.value = false;
      }
    };

    const handleDeleteKeyword = async (id) => {
      if (!isValidId(id)) {
        warnInvalidId('handleDeleteKeyword');
        return;
      }
      try {
        loading.value = true;
        await apis.keywordTenant.keywords.delete(selectedGzhName.value, id);
        fetchKeywords();
      } catch (error) {
        console.error('Error deleting tenant keyword:', error);
        errorMsg.value = '删除关键词失败: ' + (error.response?.data?.message || error.message);
      } finally {
        loading.value = false;
      }
    };

    const handleViewResponses = (record) => {
      selectedKeyword.value = record;
      fetchResponses(record.id);
      isResponseModalVisible.value = true;
    };

    const handleResponseStatusToggle = async ({ id, isActive }) => {
      if (!isValidId(id)) {
        warnInvalidId('handleResponseStatusToggle');
        return;
      }
      try {
        loadingItemId.value = id;
        await apis.keywordTenant.responses.update(selectedGzhName.value, id, { isActive });
        if (selectedKeyword.value) {
          fetchResponses(selectedKeyword.value.id);
        }
      } catch (error) {
        console.error('Error toggling tenant response status:', error);
        errorMsg.value = '更改回复状态失败: ' + (error.response?.data?.message || error.message);
      } finally {
        loadingItemId.value = null;
      }
    };

    const handleDeleteResponse = async (id) => {
      if (!isValidId(id)) {
        warnInvalidId('handleDeleteResponse');
        return;
      }
      try {
        loadingResponses.value = true;
        await apis.keywordTenant.responses.delete(selectedGzhName.value, id);
        if (selectedKeyword.value) {
          fetchResponses(selectedKeyword.value.id);
        }
      } catch (error) {
        console.error('Error deleting tenant response:', error);
        errorMsg.value = '删除回复失败: ' + (error.response?.data?.message || error.message);
      } finally {
        loadingResponses.value = false;
      }
    };

    const handleCreateResponse = async (responseData) => {
      const { keywordId, ...data } = responseData;
      if (!isValidId(keywordId)) {
        warnInvalidId('handleCreateResponse');
        return;
      }
      try {
        loadingResponses.value = true;
        errorMsg.value = '';
        await apis.keywordTenant.responses.create(selectedGzhName.value, keywordId, data);
        if (selectedKeyword.value) {
          fetchResponses(selectedKeyword.value.id);
        }
      } catch (error) {
        console.error('Error creating tenant response:', error);
        errorMsg.value = '创建回复内容失败: ' + (error.response?.data?.message || error.message);
      } finally {
        loadingResponses.value = false;
      }
    };

    const handleUpdateResponse = async (responseData) => {
      const { responseId, ...updateData } = responseData;
      if (!isValidId(responseId)) {
        warnInvalidId('handleUpdateResponse');
        return;
      }
      try {
        loadingResponses.value = true;
        errorMsg.value = '';
        await apis.keywordTenant.responses.update(selectedGzhName.value, responseId, updateData);
        if (selectedKeyword.value) {
          fetchResponses(selectedKeyword.value.id);
        }
      } catch (error) {
        console.error('Error updating tenant response:', error);
        errorMsg.value = '更新回复内容失败: ' + (error.response?.data?.message || error.message);
      } finally {
        loadingResponses.value = false;
      }
    };

    watch(previewContent, (newValue) => {
      if (newValue && (newValue.keyword || newValue.response)) {
        emit('update:selected-keyword-reply', newValue);
      }
    }, { deep: true });

    const handlePreview = async (record) => {
      if (!record) return;
      const keywordId = record.id;
      if (!isValidId(keywordId)) {
        warnInvalidId('handlePreview');
        return;
      }
      try {
        const keywordContent = record.content;

        loading.value = true;

        const response = await apis.keywordTenant.responses.getByKeywordId(selectedGzhName.value, keywordId);
        const responses = response.data?.items || [];

        if (responses.length > 0) {
          const activeResponses = responses.filter((res) => res.isActive);
          const responseToShow = activeResponses.length > 0 ? activeResponses[0] : responses[0];
          const responseContent = responseToShow.content || '预览内容';

          previewContent.value = { keyword: keywordContent, response: responseContent };
          emit('update:selected-keyword-reply', { keyword: keywordContent, response: responseContent });

          message.success('内容已发送到移动预览区域');
        } else {
          message.warning('该关键词暂无回复内容，请先添加回复。');
          isResponseModalVisible.value = true;
          selectedKeyword.value = record;
          keywordResponses.value = [];
        }
      } catch (error) {
        console.error('Error fetching tenant responses for preview:', error);
        message.error('获取预览内容失败: ' + (error.response?.data?.message || error.message));
      } finally {
        loading.value = false;
      }
    };

    // 셀렉터에서 공중계정 변경 시 그 공중계정 스코프로 재조회(欢迎语 탭과 동일 패턴)
    const onGzhChange = () => {
      pagination.current = 1;
      fetchKeywords();
    };

    // ownedGzhList prop이 변경되면(예: 초기 로딩 완료) 자동 선택 갱신
    watch(() => props.ownedGzhList, (list) => {
      if (list.length > 0 && !list.some((acc) => acc.gzhName === selectedGzhName.value)) {
        selectedGzhName.value = list[0].gzhName;
        fetchKeywords();
      }
    });

    // 선택 공중계정을 부모로 끌어올려 MobileEmulator 헤더/아바타가 Dentium 하드코딩 폴백 대신
    // 실제 선택 GZH(예: dentalmax)를 반영하도록 한다(1개 소유 자동선택 케이스 포함 즉시 emit).
    watch(selectedGzhName, (name) => {
      emit('update:selected-gzh-name', name);
    }, { immediate: true });

    onMounted(() => {
      if (selectedGzhName.value) {
        fetchKeywords();
      }
      window.addEventListener('resize', onResize);
    });

    onBeforeUnmount(() => {
      window.removeEventListener('resize', onResize);
    });

    return {
      isMobileView,
      selectedGzhName,
      keywords,
      keywordResponses,
      selectedKeyword,
      currentKeyword,
      searchKeyword,
      loading,
      loadingResponses,
      loadingItemId,
      errorMsg,
      pagination,
      columns,
      isModalVisible,
      isResponseModalVisible,
      confirmLoading,
      isEditing,
      getMatchTypeLabel,
      onGzhChange,
      fetchKeywords,
      toggleKeywordStatus,
      handleTableChange,
      handleSearch,
      showAddKeywordModal,
      handleEditKeyword,
      handleModalCancel,
      handleModalSubmit,
      handleDeleteKeyword,
      handleViewResponses,
      handleResponseStatusToggle,
      handleDeleteResponse,
      handleCreateResponse,
      handleUpdateResponse,
      handlePreview
    };
  }
};
</script>

<style lang="scss" scoped>
.keyword-reply-container {
  max-width: 100%;
}

.section-title {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 8px;
  color: #333;
}

.section-description {
  margin-bottom: 24px;
  color: #666;
  font-size: 14px;
}

.keyword-list {
  min-height: 300px;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px 0;
}

.gzh-empty-notice {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 60px 0;
}

.mb-3 {
  margin-bottom: 16px;
}

.keyword-actions {
  display: flex;
  margin-bottom: 16px;
  flex-wrap: wrap;
  gap: 8px;

  .action-button {
    height: 40px;
  }

  .search-input {
    :deep(.ant-input) {
      height: 40px;
      padding-left: 12px;
    }
    :deep(.ant-input-search-button) {
      height: 40px;
    }
  }
}

@media (max-width: 767px) {
  .keyword-actions .search-input {
    width: 100% !important;
    margin-left: 0 !important;
  }
}

:deep(.ant-popconfirm-buttons) {
  display: flex !important;
  flex-direction: row !important;
  justify-content: flex-end !important;

  .ant-btn {
    margin-left: 8px !important;
    margin-top: 0 !important;

    &:first-child {
      margin-left: 0 !important;
    }
  }
}

@media (max-width: 767px) {
  .mobile-card-list { display: flex; flex-direction: column; gap: 12px; }
  .mobile-company-card { border: 1px solid #f0f0f0; border-radius: 6px; padding: 12px; background: #fff; }
  .card-field { display: flex; gap: 8px; margin-bottom: 6px; font-size: 13px; align-items: center; }
  .card-label { font-weight: 600; min-width: 80px; flex-shrink: 0; color: #666; }
  .card-actions { display: flex; flex-wrap: wrap; gap: 6px; border-top: 1px solid #f5f5f5; padding-top: 8px; }
  .card-actions :deep(.ant-btn) { flex: 1 1 auto; }
}
</style>
